import axios from "../../services/axios.config";
import {isTimerV1Enabled} from "@/config/timerV1Config";
import * as timerV1Api from "@/services/timerV1Api";
import {
  mapV1SessionToLegacyActive,
  mergeV1ActiveIntoTimerList,
  normalizeTimerLogHours,
  resolveLogBaseHours,
  resolveSessionLogDate,
  shouldShowV1SessionOnDate,
} from "@/services/timerV1Mapper";
import {
  GetCurrentProjectTimerData,
  calculateProjectTime,
  returnHHMMSSFormat,
  HHMMSStoSeconds,
  hoursDecimalForLegacyApi,
  snapHoursToWholeSeconds,
  today,
} from "@/Utilites/timerHelper";
import {
  createLegacyRunningLog,
  hoursFromV1Session,
  patchLegacyRunningLog,
  stopLegacyTimerLogById,
} from "@/services/timerLegacySync";
import {resolveTimerUserId} from "@/services/timerActor";
import {parseNekoApiListArray} from "@/Utilites/nekoApiList";

/** Updates navbar + table running display every second while timer is on. */
let displayTickId = null;
let readTimerRequestSeq = 0;
const DISPLAY_TICK_MS = 250;

const endpoints = {
  getCurrentTimersListByDate: `api/users/timers/get-current-timer/`,
  createNewTimer: `api/users/timers/create-new-timer/`,
  updateTimerById: `api/users/timers/update-timer-by-id/`,
  deleteTimerById: `api/users/timers/delete-timer-by-id/`,
  filterTimerByDates: `api/users/timesheets/get-timesheet/filtered-data/`,
};

const state = {
  timerLogsList: [],
  currentTimerActive: "",
  currentTimerActiveTime: "00:00:00",
  monthlyData: "",
  isTimerOn: false,
  v1Session: null,
  v1LastSyncAt: null,
  /** Set while stopping so SYNC does not re-activate a stale server session. */
  v1StopPending: false,
  /** Pre-start hours for the active row (decimal); not reset by v1 elapsed. */
  v1BaseHours: 0,
  /** Last known decimal hours per log id (survives API returning 0 on start). */
  timerLogHoursCache: {},
  /** Date shown on timer page; v1 running row only merges when this matches. */
  viewingTimerLogDate: null,
  /** legacy_timer_log_id → timer_log_date (survives v1 API sync without log date). */
  v1LegacyLogDates: {},
};

function enrichRowFromHoursCache(state, row) {
  if (!row || row.id == null) return row;
  const key = String(row.id);
  if (!Object.prototype.hasOwnProperty.call(state.timerLogHoursCache, key)) {
    return row;
  }
  const h = Number(state.timerLogHoursCache[key]) || 0;
  return {...row, hours: h, hours_with_timer: h, working_hours: h};
}

const getters = {
  getTimers: (state, getters, rootState, rootGetters) => {
    const list = state.timerLogsList.map((row) =>
      enrichRowFromHoursCache(state, row)
    );
    if (!isTimerV1Enabled() || !state.v1Session) {
      return list;
    }
    if (
      !shouldShowV1SessionOnDate(
        state.v1Session,
        state.timerLogsList,
        state.viewingTimerLogDate
      )
    ) {
      return list;
    }
    return mergeV1ActiveIntoTimerList(
      list,
      state.v1Session,
      rootGetters.userProfileData,
      state.v1BaseHours,
      state.viewingTimerLogDate
    );
  },
  getCurrentActiveTimer: (state) => state.currentTimerActive,
  getCurrentActiveTimerTime: (state) => state.currentTimerActiveTime,
  getMonthlyData: (state) => state.monthlyData,
  getTimerState: (state) => state.isTimerOn,
  isTimerV1Active: () => isTimerV1Enabled(),
  getTimerV1Session: (state) => state.v1Session,
};

function legacyApiHours(hours) {
  return hoursDecimalForLegacyApi(snapHoursToWholeSeconds(hours));
}

function cacheLogHours(state, row, {force = false} = {}) {
  if (!row || row.id == null) return;
  const key = String(row.id);
  const h = snapHoursToWholeSeconds(resolveLogBaseHours(row));
  const has = Object.prototype.hasOwnProperty.call(
    state.timerLogHoursCache,
    key
  );
  if (force || h > 0 || !has) {
    state.timerLogHoursCache[key] = h;
  }
}

function resolveV1BaseHours(state, legacyId, row) {
  const fromRow = row ? resolveLogBaseHours(row) : 0;
  const key = String(legacyId || "");
  const fromCache =
    key && Object.prototype.hasOwnProperty.call(state.timerLogHoursCache, key)
      ? Number(state.timerLogHoursCache[key]) || 0
      : 0;
  return Math.max(fromRow, fromCache);
}

function hoursFromDisplayHms(hms) {
  if (hms == null || hms === "") return 0;
  const s = String(hms).trim();
  if (!s.includes(":")) return snapHoursToWholeSeconds(s);
  return snapHoursToWholeSeconds(HHMMSStoSeconds(s) / 3600);
}

/** True when `value` is an in-place edit of the timer row that is currently running. */
function isActiveRunningLogUpdate(state, value) {
  if (!value || value.id == null) return false;
  const active = state.currentTimerActive;
  if (
    active &&
    active.id != null &&
    String(active.id) === String(value.id) &&
    (value.timer_started_at != null || active.timer_started_at != null)
  ) {
    return true;
  }
  if (
    state.v1Session &&
    state.v1Session.status === "active" &&
    state.v1Session.legacy_timer_log_id != null &&
    String(state.v1Session.legacy_timer_log_id) === String(value.id)
  ) {
    return true;
  }
  return false;
}

function readCachedBaseHours(state, legacyId, row) {
  const key = String(legacyId || "");
  if (
    key &&
    Object.prototype.hasOwnProperty.call(state.timerLogHoursCache, key)
  ) {
    return Number(state.timerLogHoursCache[key]) || 0;
  }
  return resolveLogBaseHours(row);
}

/** Timer v1 is active on a different legacy log than `logId` (e.g. editing a stopped row). */
function isV1RunningOnOtherLog(state, logId) {
  if (
    !isTimerV1Enabled() ||
    !state.v1Session ||
    state.v1Session.status !== "active"
  ) {
    return false;
  }
  const activeId = state.v1Session.legacy_timer_log_id;
  if (activeId == null || logId == null) return false;
  return String(activeId) !== String(logId);
}

async function reassertV1ActiveDisplay({commit, dispatch, state, rootGetters}) {
  if (!state.v1Session || state.v1Session.status !== "active") return;
  const profile = rootGetters.userProfileData;
  const legacyId = state.v1Session.legacy_timer_log_id;
  const row =
    legacyId != null
      ? state.timerLogsList.find((r) => String(r.id) === String(legacyId))
      : null;
  const baseHours = resolveV1BaseHours(state, legacyId, row);
  commit("SET_V1_BASE_HOURS", baseHours);
  const mapped = mapV1SessionToLegacyActive(state.v1Session, profile, row);
  if (legacyId != null) mapped.id = legacyId;
  mapped.hours = baseHours;
  mapped.hours_with_timer = baseHours;
  mapped.working_hours = baseHours;
  commit("CLEAR_OTHER_RUNNING_LOGS", mapped.id);
  commit("ACTIVATE_TIMER", mapped);
  await dispatch("TICK_ACTIVE_DISPLAY");
  await dispatch("START_DISPLAY_TICK");
}

async function syncLegacyLogLink(session, legacyLogId) {
  if (!session || !session.id || legacyLogId == null) return;
  try {
    await timerV1Api.linkLegacyLog(session.id, legacyLogId);
  } catch (e) {
    console.warn("[timer v1] link legacy log failed", e);
  }
}

const actions = {
  SYNC_TIMER_V1_SESSION: async ({commit, state, rootGetters, dispatch}) => {
    if (!isTimerV1Enabled()) return;
    const profile = rootGetters.userProfileData;
    if (!profile || profile.user_id == null) return;
    try {
      const active = await timerV1Api.fetchMyActiveSession();
      commit("SET_V1_SESSION", active);
      commit("SET_V1_SYNC_AT", new Date().toISOString());
      if (active && state.v1StopPending) {
        try {
          await timerV1Api.stopSession({session_id: active.id});
        } catch (e) {
          console.warn("[timer v1] retry stop during sync failed", e);
        }
        commit("SET_V1_STOP_PENDING", false);
        commit("SET_V1_SESSION", null);
        return;
      }
      if (active) {
        if (
          !shouldShowV1SessionOnDate(
            active,
            state.timerLogsList,
            state.viewingTimerLogDate
          )
        ) {
          commit("SET_V1_SESSION", active);
          return;
        }
        if (state.timerLogsList.length === 0) {
          commit("SET_V1_SESSION", active);
          return;
        }
        const legacyId = active.legacy_timer_log_id;
        const row =
          legacyId != null
            ? state.timerLogsList.find((r) => String(r.id) === String(legacyId))
            : null;
        if (legacyId != null && !row) {
          const sessionDate = resolveSessionLogDate(
            active,
            state.timerLogsList
          );
          if (
            sessionDate &&
            state.viewingTimerLogDate &&
            sessionDate !== state.viewingTimerLogDate
          ) {
            commit("SET_V1_SESSION", active);
            return;
          }
        }
        const baseHours = resolveV1BaseHours(state, legacyId, row);
        commit("SET_V1_BASE_HOURS", baseHours);
        const mapped = mapV1SessionToLegacyActive(active, profile, row);
        if (legacyId != null) mapped.id = legacyId;
        mapped.hours = baseHours;
        mapped.hours_with_timer = baseHours;
        mapped.working_hours = baseHours;
        commit("CLEAR_OTHER_RUNNING_LOGS", mapped.id);
        commit("ACTIVATE_TIMER", mapped);
        await dispatch("TICK_ACTIVE_DISPLAY");
        await dispatch("START_DISPLAY_TICK");
      } else if (state.isTimerOn) {
        await dispatch("STOP_DISPLAY_TICK");
        commit("DEACTIVATE_TIMER");
      }
    } catch (e) {
      console.warn("[timer v1] sync failed", e);
    }
  },

  TIMER_V1_START: async (
    {commit, dispatch, state, rootGetters},
    payload = {}
  ) => {
    commit("SET_V1_STOP_PENDING", false);
    if (state.v1Session) {
      await dispatch("TIMER_V1_STOP", {});
    }

    const profile = rootGetters.userProfileData;
    const actorUserId = resolveTimerUserId(profile);
    const legacyId = payload.legacy_timer_log_id;
    const existingRow =
      legacyId != null
        ? state.timerLogsList.find((r) => String(r.id) === String(legacyId))
        : null;
    // Per-row cache + list row only — never reuse v1BaseHours from the previous active timer.
    let baseHours = snapHoursToWholeSeconds(
      readCachedBaseHours(state, legacyId, existingRow)
    );
    if (legacyId != null) {
      commit("CACHE_TIMER_LOG_HOURS", {id: legacyId, hours: baseHours});
    }
    commit("SET_V1_BASE_HOURS", baseHours);

    const timerLogDate =
      payload.timer_log_date ||
      (existingRow && (existingRow.timer_log_date || existingRow.log_date)) ||
      state.viewingTimerLogDate ||
      today();

    const session = await timerV1Api.startSession({
      project_id: payload.project_id,
      task_description: payload.task_description,
      legacy_timer_log_id: legacyId,
      timer_log_date: timerLogDate,
    });

    let legacyLog = null;
    try {
      if (legacyId != null && legacyId !== "") {
        legacyLog =
          state.timerLogsList.find((r) => String(r.id) === String(legacyId)) ||
          null;
        await syncLegacyLogLink(session, legacyId);
        session.legacy_timer_log_id = legacyId;
        // Do not PATCH legacy on start — nekoapi often zeroes hours; sync on stop only.
      } else {
        if (actorUserId == null) {
          throw new Error(
            "Cannot start timer: signed-in user id missing from JWT"
          );
        }
        legacyLog = await createLegacyRunningLog({
          userId: actorUserId,
          projectId: payload.project_id,
          taskDescription: payload.task_description,
          startedAt: session.started_at,
          timerLogDate,
        });
        await syncLegacyLogLink(session, legacyLog.id);
        commit("ADD_TIMER_DATA", legacyLog);
        cacheLogHours(state, legacyLog);
        session.legacy_timer_log_id = legacyLog.id;
      }
    } catch (e) {
      console.warn("[timer v1] legacy row on start failed", e);
    }

    const rowForDate = legacyLog || existingRow;
    const resolvedLogDate =
      timerLogDate ||
      (rowForDate &&
        (rowForDate.timer_log_date || rowForDate.log_date || null));
    if (resolvedLogDate && legacyId != null) {
      commit("CACHE_V1_LEGACY_LOG_DATE", {id: legacyId, date: resolvedLogDate});
    }
    commit("SET_V1_SESSION", {
      ...session,
      legacy_timer_log_id:
        session.legacy_timer_log_id || (legacyLog && legacyLog.id) || legacyId,
      timer_log_date: resolvedLogDate || session.timer_log_date,
    });

    const mapped = mapV1SessionToLegacyActive(
      session,
      profile,
      legacyLog || existingRow
    );
    if (legacyId != null) mapped.id = legacyId;
    else if (legacyLog && legacyLog.id != null) mapped.id = legacyLog.id;
    mapped.hours = baseHours;
    mapped.hours_with_timer = baseHours;
    mapped.working_hours = baseHours;
    if (resolvedLogDate) {
      mapped.timer_log_date = resolvedLogDate;
      mapped.log_date = resolvedLogDate;
    }

    commit("CLEAR_OTHER_RUNNING_LOGS", mapped.id);
    commit("ACTIVATE_TIMER", mapped);
    await dispatch("TICK_ACTIVE_DISPLAY");
    await dispatch("START_DISPLAY_TICK");
    return session;
  },

  TIMER_V1_STOP: async (
    {commit, state, dispatch, rootGetters},
    payload = {}
  ) => {
    commit("SET_V1_STOP_PENDING", true);
    const legacyId =
      (state.v1Session && state.v1Session.legacy_timer_log_id) ||
      (state.currentTimerActive &&
        state.currentTimerActive.id != null &&
        state.currentTimerActive.id !== "" &&
        state.currentTimerActive.id) ||
      null;
    let session = null;
    try {
      session = await timerV1Api.stopSession({
        session_id:
          payload.session_id || (state.v1Session && state.v1Session.id),
        notes: payload.notes,
      });
    } catch (e) {
      console.warn("[timer v1] stop API failed", e);
    }
    const legacyRow =
      legacyId != null
        ? state.timerLogsList.find((r) => String(r.id) === String(legacyId))
        : null;
    try {
      if (legacyId != null && session) {
        const cachedBase = resolveV1BaseHours(state, legacyId, legacyRow);
        const fromSession = hoursFromV1Session(session);
        let totalHours = snapHoursToWholeSeconds(cachedBase + fromSession);
        const active = state.currentTimerActive;
        const displayMatches =
          active && legacyId != null && String(active.id) === String(legacyId);
        if (displayMatches && state.currentTimerActiveTime) {
          const fromDisplay = hoursFromDisplayHms(state.currentTimerActiveTime);
          if (fromDisplay > totalHours) {
            totalHours = snapHoursToWholeSeconds(fromDisplay);
          }
        }
        const stoppedFields = {
          timer_started_at: null,
          hours: totalHours,
          hours_with_timer: totalHours,
          working_hours: totalHours,
        };
        const actorUserId = resolveTimerUserId(rootGetters.userProfileData);
        const updated = await stopLegacyTimerLogById(
          legacyId,
          totalHours,
          legacyRow,
          actorUserId
        );
        if (legacyRow) {
          if (updated) {
            commit(
              "UPDATE_TIMER_DATA",
              normalizeTimerLogHours({
                ...legacyRow,
                ...updated,
                ...stoppedFields,
              })
            );
          } else {
            commit(
              "UPDATE_TIMER_DATA",
              normalizeTimerLogHours({...legacyRow, ...stoppedFields})
            );
          }
        }
        commit("CACHE_TIMER_LOG_HOURS", {
          id: legacyId,
          hours: snapHoursToWholeSeconds(totalHours),
          force: true,
        });
      }
    } catch (e) {
      console.warn("[timer v1] legacy row on stop failed", e);
    }
    if (legacyId != null && legacyId !== "") {
      commit("CLEAR_RUNNING_ON_LOG", legacyId);
    }
    commit("SET_V1_SESSION", null);
    commit("SET_V1_BASE_HOURS", 0);
    commit("SET_V1_STOP_PENDING", false);
    await dispatch("STOP_DISPLAY_TICK");
    commit("DEACTIVATE_TIMER");
    return session;
  },

  START_DISPLAY_TICK: ({dispatch}) => {
    dispatch("STOP_DISPLAY_TICK");
    dispatch("TICK_ACTIVE_DISPLAY");
    displayTickId = setInterval(() => {
      dispatch("TICK_ACTIVE_DISPLAY");
    }, DISPLAY_TICK_MS);
    [50, 150, 400].forEach((ms) => {
      setTimeout(() => dispatch("TICK_ACTIVE_DISPLAY"), ms);
    });
  },

  STOP_DISPLAY_TICK: () => {
    if (displayTickId != null) {
      clearInterval(displayTickId);
      displayTickId = null;
    }
  },

  TICK_ACTIVE_DISPLAY: ({commit, state, dispatch}) => {
    if (!state.isTimerOn) {
      dispatch("STOP_DISPLAY_TICK");
      return;
    }
    let active = state.currentTimerActive;
    if (
      active &&
      active !== "" &&
      active.id == null &&
      state.v1Session &&
      state.v1Session.legacy_timer_log_id != null
    ) {
      active = {...active, id: state.v1Session.legacy_timer_log_id};
    }
    if (!active || active === "" || active.id == null) {
      return;
    }
    let baseH = Number(active.hours) || 0;
    let began = active.timer_started_at;
    if (
      isTimerV1Enabled() &&
      state.v1Session &&
      state.v1Session.status === "active" &&
      state.v1Session.legacy_timer_log_id != null &&
      String(active.id) === String(state.v1Session.legacy_timer_log_id)
    ) {
      baseH = state.v1BaseHours > 0 ? state.v1BaseHours : baseH;
      began = state.v1Session.started_at || began;
    }
    if (!began) return;
    const parts = GetCurrentProjectTimerData(baseH);
    const timer = calculateProjectTime(began, parts);
    commit("SET_CURRENT_TIMER_TIME", returnHHMMSSFormat(timer));
  },

  CREATE_TIMER_REQUEST: async ({commit, dispatch, rootGetters}, value) => {
    if (isTimerV1Enabled() && value && value.timer_started_at != null) {
      await dispatch("TIMER_V1_START", {
        project_id: value.project_id,
        task_description: value.task_description,
        timer_log_date: value.timer_log_date || value.log_date,
      });
      return;
    }
    const actorUserId = resolveTimerUserId(rootGetters.userProfileData);
    const body = {...value};
    if (actorUserId != null) {
      body.user_id = actorUserId;
    } else if (body.user_id == null || body.user_id === "") {
      throw new Error(
        "Cannot create timer log: signed-in user id missing from JWT"
      );
    }
    if (body.hours != null || body.working_hours != null) {
      const h = legacyApiHours(resolveLogBaseHours(body));
      body.hours = h;
      body.hours_with_timer = h;
      body.working_hours = h;
    }
    let response = await axios.post(endpoints.createNewTimer, {...body});
    if (response.status === 201) {
      const row = normalizeTimerLogHours(response.data);
      if (value.timer_started_at !== null) {
        commit("ACTIVATE_TIMER", row);
        await dispatch("START_DISPLAY_TICK");
      }
      commit("ADD_TIMER_DATA", row);
      return response.data;
    }
    return null;
  },

  FILTER_TIMER_REQUEST: async ({commit}, filterQuery) => {
    let response = await axios.get(
      endpoints.filterTimerByDates + `?${filterQuery}`
    );
    if (response.status === 200) {
      commit("SET_MONTHLY_DATA", response.data);
    }
  },

  READ_TIMER_REQUEST: async ({commit, dispatch}, value) => {
    const reqId = ++readTimerRequestSeq;
    commit("SET_VIEWING_TIMER_LOG_DATE", value.date);
    let response = await axios.get(
      endpoints.getCurrentTimersListByDate +
        `?user_id=${value.userId}&timer_log_date=${value.date}`
    );
    if (reqId !== readTimerRequestSeq) {
      return;
    }
    if (response.status === 200) {
      commit("SET_TIMER_DATA", parseNekoApiListArray(response.data));
    }
    if (isTimerV1Enabled()) {
      await dispatch("SYNC_TIMER_V1_SESSION");
    }
  },

  UPDATE_TIMER_REQUEST: async (
    {commit, dispatch, state, rootGetters},
    value
  ) => {
    if (
      isTimerV1Enabled() &&
      value &&
      value.timer_started_at === null &&
      state.v1Session
    ) {
      const activeLogId =
        state.currentTimerActive && state.currentTimerActive.id;
      if (activeLogId != null && String(activeLogId) === String(value.id)) {
        await dispatch("TIMER_V1_STOP", {
          session_id: state.v1Session.id,
          project_id: value.project_id,
          task_description: value.task_description,
        });
        return;
      }
    }
    if (isTimerV1Enabled() && value && isActiveRunningLogUpdate(state, value)) {
      const row =
        state.timerLogsList.find((r) => String(r.id) === String(value.id)) ||
        value;
      const baseHours = snapHoursToWholeSeconds(
        readCachedBaseHours(state, value.id, row)
      );
      const merged = {
        ...row,
        ...value,
        hours: baseHours,
        hours_with_timer: baseHours,
        working_hours: baseHours,
      };
      let patched = null;
      try {
        patched = await patchLegacyRunningLog(merged, {
          startedAt:
            merged.timer_started_at ||
            (state.v1Session && state.v1Session.started_at),
          projectId: value.project_id,
          taskDescription: value.task_description,
          baseHours,
        });
      } catch (e) {
        console.warn("[timer v1] running log metadata update failed", e);
        throw e;
      }
      const normalized = normalizeTimerLogHours({
        ...merged,
        ...(patched || {}),
      });
      commit("UPDATE_TIMER_DATA", normalized);
      if (state.v1Session) {
        commit("SET_V1_SESSION", {
          ...state.v1Session,
          project_id:
            value.project_id != null && value.project_id !== ""
              ? value.project_id
              : state.v1Session.project_id,
          task_description:
            value.task_description != null
              ? String(value.task_description)
              : state.v1Session.task_description,
        });
      }
      const profile = rootGetters.userProfileData;
      const mapped = mapV1SessionToLegacyActive(
        state.v1Session || {
          started_at: normalized.timer_started_at,
          project_id: normalized.project_id,
          task_description: normalized.task_description,
        },
        profile,
        normalized
      );
      mapped.id = value.id;
      mapped.hours = baseHours;
      mapped.hours_with_timer = baseHours;
      mapped.working_hours = baseHours;
      mapped.task_description = normalized.task_description;
      mapped.log_description =
        normalized.log_description || normalized.task_description;
      commit("ACTIVATE_TIMER", mapped);
      await dispatch("START_DISPLAY_TICK");
      return;
    }
    if (isTimerV1Enabled() && value && value.timer_started_at != null) {
      await dispatch("TIMER_V1_START", {
        project_id: value.project_id,
        task_description: value.task_description,
        legacy_timer_log_id: value.id,
        timer_log_date: value.timer_log_date || value.log_date,
      });
      return;
    }
    const h = legacyApiHours(resolveLogBaseHours(value));
    const payload = {
      ...value,
      hours: h,
      hours_with_timer: h,
      working_hours: h,
    };
    let response = await axios.patch(
      endpoints.updateTimerById + `${value.id}`,
      payload
    );
    if (response.status === 200) {
      const merged = {
        ...payload,
        ...(response.data && typeof response.data === "object"
          ? response.data
          : {}),
      };
      const normalized = normalizeTimerLogHours(merged);
      commit("UPDATE_TIMER_DATA", normalized);
      if (value.timer_started_at !== null) {
        commit("ACTIVATE_TIMER", normalized);
        await dispatch("START_DISPLAY_TICK");
      } else if (isV1RunningOnOtherLog(state, value.id)) {
        await reassertV1ActiveDisplay({commit, dispatch, state, rootGetters});
      } else {
        await dispatch("STOP_DISPLAY_TICK");
        commit("DEACTIVATE_TIMER");
      }
    }
  },

  DELETE_TIMER_REQUEST: async ({commit}, id) => {
    let response = await axios.delete(endpoints.deleteTimerById + `${id}`);
    if (response.status === 204) {
      commit("DELETE_TIMER_DATA", id);
    }
  },

  SET_TIMER_TIME: (context, timeString) => {
    context.commit("SET_CURRENT_TIMER_TIME", timeString);
  },
};

const mutations = {
  SET_TIMER_DATA: (state, value) => {
    state.timerLogsList = Array.isArray(value)
      ? value.map((row) => normalizeTimerLogHours(row))
      : [];
    state.timerLogsList.forEach((row) => {
      cacheLogHours(state, row, {force: true});
      const d = row.timer_log_date || row.log_date;
      if (row.id != null && d) {
        state.v1LegacyLogDates[String(row.id)] = d;
      }
    });
  },
  SET_MONTHLY_DATA: (state, value) => {
    state.monthlyData = value;
  },
  CACHE_V1_LEGACY_LOG_DATE: (state, {id, date}) => {
    if (id == null || !date) return;
    state.v1LegacyLogDates[String(id)] = date;
  },
  SET_V1_SESSION: (state, session) => {
    if (!session) {
      state.v1Session = null;
      return;
    }
    const legacyId = session.legacy_timer_log_id;
    let timerLogDate = session.timer_log_date || null;
    if (!timerLogDate && legacyId != null) {
      timerLogDate = state.v1LegacyLogDates[String(legacyId)] || null;
      if (
        !timerLogDate &&
        state.v1Session &&
        String(state.v1Session.legacy_timer_log_id) === String(legacyId)
      ) {
        timerLogDate = state.v1Session.timer_log_date || null;
      }
    }
    if (timerLogDate && legacyId != null) {
      state.v1LegacyLogDates[String(legacyId)] = timerLogDate;
    }
    state.v1Session = timerLogDate
      ? {...session, timer_log_date: timerLogDate}
      : session;
  },
  SET_V1_SYNC_AT: (state, iso) => {
    state.v1LastSyncAt = iso;
  },
  SET_V1_BASE_HOURS: (state, hours) => {
    state.v1BaseHours = Number(hours) || 0;
  },
  SET_V1_STOP_PENDING: (state, value) => {
    state.v1StopPending = Boolean(value);
  },
  SET_VIEWING_TIMER_LOG_DATE: (state, date) => {
    state.viewingTimerLogDate = date || null;
  },
  UPDATE_TIMER_DATA: (state, data) => {
    const i = state.timerLogsList.findIndex(
      (item) => String(item.id) === String(data.id)
    );
    if (i === -1) return;
    const normalized = normalizeTimerLogHours(data);
    state.timerLogsList.splice(i, 1, normalized);
    cacheLogHours(state, normalized, {force: true});
  },
  CACHE_TIMER_LOG_HOURS: (state, {id, hours, force}) => {
    if (id == null) return;
    const key = String(id);
    const h = snapHoursToWholeSeconds(hours);
    const prev = state.timerLogHoursCache[key];
    if (!force && h === 0 && prev > 0) return;
    state.timerLogHoursCache[key] = h;
  },

  ADD_TIMER_DATA: (state, value) => {
    const normalized = normalizeTimerLogHours(value);
    state.timerLogsList.push(normalized);
    cacheLogHours(state, normalized);
  },
  SET_CURRENT_TIMER_TIME: (state, value) => {
    state.currentTimerActiveTime = value;
  },
  DELETE_TIMER_DATA: (state, id) => {
    const i = state.timerLogsList.findIndex(
      (item) => String(item.id) === String(id)
    );
    if (i === -1) return;
    const key = String(id);
    delete state.timerLogHoursCache[key];
    state.timerLogsList.splice(i, 1);
  },

  ACTIVATE_TIMER: (state, value) => {
    state.currentTimerActive = value;
    state.isTimerOn = true;
  },

  DEACTIVATE_TIMER: (state) => {
    state.currentTimerActive = "";
    state.isTimerOn = false;
    state.currentTimerActiveTime = "00:00:00";
  },

  /** Only one row may show Running when using timer v1. */
  CLEAR_OTHER_RUNNING_LOGS: (state, activeLogId) => {
    const aid = String(activeLogId || "");
    state.timerLogsList = state.timerLogsList.map((row) => {
      if (aid && String(row.id) === aid) return row;
      if (row.timer_started_at != null) {
        return {...row, timer_started_at: null};
      }
      return row;
    });
  },

  CLEAR_RUNNING_ON_LOG: (state, logId) => {
    const key = String(logId || "");
    if (!key) return;
    state.timerLogsList = state.timerLogsList.map((row) => {
      if (String(row.id) !== key) return row;
      return {...row, timer_started_at: null};
    });
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
